import React from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { StatusTone } from '../guideTokens';
import './GuideComponents.css';

/** Color del monto: el del tono de estado de la guía; `neutral` usa el texto principal. */
export type GuideKpiTone = Exclude<StatusTone, 'brand'>;

export interface GuideKpiCardProps {
  /** Rótulo ("Por cobrar"), texto secundario. */
  label: React.ReactNode;
  /** Ayuda junto al rótulo, normalmente un `GuideHelpPopover`. */
  help?: React.ReactNode;
  /** Monto o número grande (28 / Bold / tabulares). */
  value: React.ReactNode;
  /** Tono del monto (alerta para lo pendiente, éxito, info, ...). Por defecto `neutral`. */
  tone?: GuideKpiTone;
  /** Detalle secundario debajo ("8 liquidaciones"). */
  detail?: React.ReactNode;
  /** Contenido extra al pie (p. ej. `GuideCountChip`). */
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  /** `data-testid` del monto. */
  valueTestId?: string;
}

/**
 * Tarjeta KPI de la guía ("Componentes" → Tarjetas): rótulo secundario, monto grande bold en el color
 * del estado y detalle secundario. Superficie con borde, radio 4 y padding 16 / 20. Para varias
 * tarjetas en fila, envolver en `GuideKpiGrid`.
 */
export const GuideKpiCard = ({ label, help, value, tone = 'neutral', detail, children, className, 'data-testid': testId, valueTestId }: GuideKpiCardProps) => (
  <div className={mergeClasses('ichtys-kpi', className)} data-tone={tone} data-testid={testId}>
    <span className="ichtys-kpi__label">
      {label}
      {help}
    </span>
    <span className="ichtys-kpi__value" data-testid={valueTestId}>
      {value}
    </span>
    {detail && <span className="ichtys-kpi__detail">{detail}</span>}
    {children && <div className="ichtys-kpi__extra">{children}</div>}
  </div>
);

/** Grilla adaptable de tarjetas KPI (mínimo 220 px por tarjeta, separación 16). */
export const GuideKpiGrid = ({ children, className, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...rest} className={mergeClasses('ichtys-kpi-grid', className)}>
    {children}
  </div>
);

/**
 * Conteo en pastilla ("Sin revisar 12"): texto y contador en badge tenue. Sólo muestra datos; para
 * un filtro clickeable usar un botón con el mismo aspecto.
 */
export const GuideCountChip = ({ label, count, className }: { label: React.ReactNode; count: React.ReactNode; className?: string }) => (
  <span className={mergeClasses('ichtys-count-chip', className)}>
    {label}
    <b className="ichtys-count-chip__count">{count}</b>
  </span>
);

export default GuideKpiCard;
